/**
 * Publish Service
 * 
 * Handles publishing focus lists with audit trail.
 * Makes E2 (Arrangement yields publishable priority lists) and 
 * H2 (Governance controls publish) true.
 */

import { getSupabaseClient } from './supabaseClient';
import { getOrCreateSupabaseUser } from './userSync';
import { generateUUID } from './supabaseSync';
import { roleHasCapability } from '../types/prioritization/rolePermissionMapping';
import { checkPolicyForList } from '../lib/authorization/storeEnforcement';
import { getReasonMessage } from '../lib/authorization/reasonMessages';
import { isPoolScoped } from './poolScope';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { RationaleParts } from './rationaleParts';
import type { Json } from '@/types/database.generated';

export type PublishStatus = 'draft' | 'pending_approval' | 'published' | 'archived';

export interface PublishEvent {
  id: string;
  itemPoolId: string;
  previousStatus: PublishStatus;
  newStatus: PublishStatus;
  publishedBy: string;
  publishedAt: string;
  approvedBy?: string;
  rationale?: string;
  metadata?: Record<string, unknown>;
}

export interface PublishAttemptResult {
  success: boolean;
  message: string;
  newStatus?: PublishStatus;
  requiresApproval?: boolean;
  /**
   * The server answered `already_published`: it recorded NOTHING new. The
   * published version is unchanged and edits made since are not part of it.
   * (A server-side re-publish RPC does not exist yet.)
   */
  alreadyPublished?: boolean;
}

/** Why an unscoped pool cannot be published. Shown verbatim to the user. */
export const PUBLISH_REQUIRES_SCOPE_MESSAGE =
  'Assign this pool to a cycle or track before publishing. A pool with no home has no criteria set and nothing to feed.';

/** Why an unlocked pool cannot be published. Shown verbatim to the user. */
export const PUBLISH_REQUIRES_LOCK_MESSAGE =
  'Lock this pool before publishing. Publishing an unlocked pool would publish a list that can still change.';

/**
 * Is the pool itself ready to be published? Separate from who may publish
 * (`canUserPublish`). Doc 03 §2.11A: scope is required at lock; publication
 * comes after lock, so a publishable pool is scoped AND locked.
 */
export function checkPoolPublishReadiness(pool: {
  cycleId?: string | null;
  trackId?: string | null;
  poolLockedAt?: Date | string | null;
}): { allowed: boolean; reason: string } {
  if (!isPoolScoped(pool)) return { allowed: false, reason: PUBLISH_REQUIRES_SCOPE_MESSAGE };
  if (!pool.poolLockedAt) return { allowed: false, reason: PUBLISH_REQUIRES_LOCK_MESSAGE };
  return { allowed: true, reason: 'Pool is scoped and locked' };
}

/**
 * Check if user can publish (governance check)
 */
export function canUserPublish(
  userRole: string,
  // TODO: userTier is captured but never applied — this authorization check
  // currently has no tier-based gating even though tier is passed in.
  // Confirm whether that's intentional or a missing rule before relying on it.
  _userTier: number,
  listOwnerId: string,
  userId: string,
  options?: {
    listId?: string;
    workspaceId?: string | null;
    isPublished?: boolean;
  }
): { allowed: boolean; reason: string } {
  if (!userId) return { allowed: false, reason: 'Not signed in' };

  if (options?.listId) {
    const decision = checkPolicyForList('lists.publish', options.listId, {
      id: options.listId,
      ownerId: listOwnerId,
      createdBy: listOwnerId,
      workspaceId: options.workspaceId ?? undefined,
      isPublished: options.isPublished ?? false,
    });

    if (!decision.allowed) {
      const code = decision.reasons[0]?.code;
      return {
        allowed: false,
        reason: code ? getReasonMessage(code) : 'Insufficient permissions to publish',
      };
    }

    return { allowed: true, reason: 'Allowed by canonical list publish policy' };
  }

  // List owner can always publish their own list (covers demo and role-not-loaded)
  if (listOwnerId && listOwnerId === userId) {
    return { allowed: true, reason: 'Owner of this list' };
  }
  // Demo/system-owned list: allow any signed-in user to publish
  if (!listOwnerId || listOwnerId === 'system') {
    return { allowed: true, reason: 'List has no specific owner' };
  }

  // System admins can always publish
  if (userRole === 'system-admin') {
    return { allowed: true, reason: 'Role has publish authority' };
  }

  // Capability-based: LEGITIMACY_ADVANCE grants publish (legitimacy advancement)
  if (roleHasCapability(userRole, 'LEGITIMACY_ADVANCE')) {
    return { allowed: true, reason: 'Role has publish capability (LEGITIMACY_ADVANCE)' };
  }

  return { allowed: false, reason: 'Insufficient permissions to publish' };
}

/** Pool state captured on the publication event so trails can compute entry ranks. */
export interface PublishSnapshot {
  priorityIds: string[];
  version?: number;
  cutLinePosition?: number | null;
}

/**
 * Attempt to publish an item pool.
 *
 * Publication is a governance transition, so it goes through the
 * `publish_item_pool` RPC (migration 693) rather than a client insert plus a
 * separate client update: the `published_at` / `published_by` stamp and the
 * `pool_published` governance event land in one transaction. The canon-27
 * bridge (migration 584) still memorializes that event as an authoritative
 * Decision, and the stamp is what lets the client re-arm the reorder-rationale
 * gate after a reload (finding F12).
 *
 * `rationaleParts` (migration 985) is the four-part rationale written at the
 * moment: it rides on the pool_published event and the bridge writes it onto
 * that Decision. Absent when nothing was written.
 */
export async function publishItemPool(
  itemPoolId: string,
  itemPoolName: string,
  previousStatus: PublishStatus,
  clerkUserId: string,
  rationale?: string,
  snapshot?: PublishSnapshot,
  rationaleParts?: RationaleParts | null
): Promise<PublishAttemptResult> {
  void previousStatus;
  void snapshot;
  void clerkUserId;

  try {
    const { data, error } = await getTypedSupabaseClient().rpc('publish_item_pool', {
      p_pool_id: itemPoolId,
      p_rationale: rationale?.trim() || undefined,
      p_rationale_parts: rationaleParts && Object.keys(rationaleParts).length > 0 ? (rationaleParts as Json) : undefined,
    });

    if (error) {
      return {
        success: false,
        message: error.message || `Could not publish "${itemPoolName}".`,
      };
    }

    const result = (data ?? {}) as { success?: boolean; error?: string; already_published?: boolean };
    if (!result.success) {
      return {
        success: false,
        message: result.error || `Could not publish "${itemPoolName}".`,
      };
    }

    if (result.already_published) {
      return {
        success: true,
        alreadyPublished: true,
        message: `"${itemPoolName}" is already published. The published version is unchanged — edits made since it was published are not part of it.`,
        newStatus: 'published',
      };
    }

    return {
      success: true,
      message: `Item pool "${itemPoolName}" has been published`,
      newStatus: 'published',
    };
  } catch (e) {
    // The server never recorded a publication, so nothing was published.
    // Saying otherwise would leave a pool "published" on this screen only.
    console.warn('Publish: RPC unavailable — nothing was published:', e);
    const detail = e instanceof Error && e.message ? e.message : 'the server could not be reached';
    return {
      success: false,
      message: `"${itemPoolName}" was not published: ${detail}`,
    };
  }
}

/** @deprecated Use publishItemPool */
export const publishFocusList = publishItemPool;

/**
 * Request approval before publishing (for governance workflow).
 */
export async function requestPublishApproval(
  itemPoolId: string,
  itemPoolName: string,
  clerkUserId: string,
  rationale?: string
): Promise<PublishAttemptResult> {
  const supabase = getSupabaseClient();
  let createdBy: string = clerkUserId;
  try {
    createdBy = await getOrCreateSupabaseUser(clerkUserId);
  } catch {
    // use clerk id for audit
  }

  try {
    const payload = {
      itemPoolId,
      itemPoolName,
      requestedStatus: 'published',
      rationale,
      requiresApproval: true,
    };
    const { error: govError } = await supabase.from('governance_events').insert({
      id: generateUUID(),
      event_type: 'PublishAttempted',
      occurred_at: new Date().toISOString(),
      actor_id: createdBy,
      note: `Publish requested for "${itemPoolName}"`,
      after_state_snapshot: payload,
    });
    if (govError) console.error('Failed to record publish-request governance event:', govError);

    return {
      success: true,
      message: 'Publish request submitted for approval',
      newStatus: 'pending_approval',
      requiresApproval: true,
    };
  } catch (error) {
    console.error('Approval request failed:', error);
    return {
      success: false,
      message: 'Failed to submit approval request',
    };
  }
}

/**
 * Approve a pending publish request.
 */
export async function approvePublish(
  itemPoolId: string,
  itemPoolName: string,
  clerkUserId: string,
  originalRequesterId: string
): Promise<PublishAttemptResult> {
  const supabase = getSupabaseClient();
  let createdBy: string = clerkUserId;
  try {
    createdBy = await getOrCreateSupabaseUser(clerkUserId);
  } catch {
    // use clerk id for audit
  }

  try {
    const payload = {
      itemPoolId,
      itemPoolName,
      previousStatus: 'pending_approval',
      newStatus: 'published',
      approvedBy: createdBy,
      originalRequester: originalRequesterId,
    };
    const { error: govError } = await supabase.from('governance_events').insert({
      id: generateUUID(),
      event_type: 'PublishCompleted',
      occurred_at: new Date().toISOString(),
      actor_id: createdBy,
      note: `Approved and published "${itemPoolName}"`,
      after_state_snapshot: payload,
    });
    if (govError) console.error('Failed to record approval governance event:', govError);

    return {
      success: true,
      message: `Item pool "${itemPoolName}" has been approved and published`,
      newStatus: 'published',
    };
  } catch (error) {
    console.error('Approval failed:', error);
    return {
      success: false,
      message: 'Failed to approve publish',
    };
  }
}

/**
 * Archive a published item pool.
 */
export async function archiveItemPool(
  itemPoolId: string,
  itemPoolName: string,
  previousStatus: PublishStatus,
  clerkUserId: string,
  rationale?: string
): Promise<PublishAttemptResult> {
  const supabase = getSupabaseClient();
  let createdBy: string | null = null;
  try {
    createdBy = await getOrCreateSupabaseUser(clerkUserId);
  } catch (e) {
    console.warn('Archive: could not resolve Supabase user, using Clerk id for audit:', e);
    createdBy = clerkUserId;
  }

  try {
    const payload = {
      itemPoolId,
      itemPoolName,
      previousStatus,
      newStatus: 'archived',
      rationale,
    };
    const { error: govError } = await supabase.from('governance_events').insert({
      id: generateUUID(),
      event_type: 'FocusListArchived',
      occurred_at: new Date().toISOString(),
      actor_id: createdBy,
      note: `Archived list "${itemPoolName}"`,
      after_state_snapshot: payload,
    });
    if (govError) console.error('Failed to record archive governance event:', govError);

    return {
      success: true,
      message: `Item pool "${itemPoolName}" has been archived`,
      newStatus: 'archived',
    };
  } catch (error) {
    console.error('Archive failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Failed to archive item pool',
    };
  }
}

/** @deprecated Use archiveItemPool */
export const archiveFocusList = archiveItemPool;

/**
 * Get publish history for an item pool.
 */
export async function getPublishHistory(itemPoolId: string): Promise<PublishEvent[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from('governance_events')
    .select('id, event_type, occurred_at, actor_id, after_state_snapshot')
    .in('event_type', ['PublishAttempted', 'PublishCompleted', 'FocusListArchived'])
    .filter('after_state_snapshot->>itemPoolId', 'eq', itemPoolId)
    .order('occurred_at', { ascending: false });

  if (error) {
    console.error('Failed to get publish history:', error);
    return [];
  }

  const snapshot = (row: { after_state_snapshot?: Record<string, unknown> | null }) => row.after_state_snapshot ?? {};
  return (data || []).map((row: { id: string; event_type: string; occurred_at: string; actor_id?: string | null; after_state_snapshot?: Record<string, unknown> | null }) => ({
    id: row.id,
    itemPoolId: (snapshot(row).itemPoolId as string) ?? '',
    previousStatus: snapshot(row).previousStatus as PublishEvent['previousStatus'],
    newStatus: snapshot(row).newStatus as PublishEvent['newStatus'],
    publishedBy: row.actor_id ?? '',
    publishedAt: row.occurred_at ?? '',
    approvedBy: snapshot(row).approvedBy as string | undefined,
    rationale: snapshot(row).rationale as string | undefined,
    metadata: snapshot(row) as Record<string, unknown>,
  }));
}


